import { ContactFormData, InterestForm, MunicipalitiesInterestForm, SuggestionForm } from "@/models/customersTypes";

export type MunicipalityUiForm = {
  location: string;
  postal_code: string;
  population: number | "";
  contact_name: string;
  contact_role: string;
  email: string;
  phone: string | null;
  local_supplies: (
    | "Bäcker"
    | "Metzger"
    | "Gemüseladen"
    | "Lebensmittelladen"
    | "Supermarkt"
    | "Keine"
  )[];
  kms_next_supply: string | null;
  motivation: (
    | "Nahversorgung sichern"
    | "Ortsmitte stärken"
    | "Ältere Menschen versorgen"
    | "Regionalität fördern"
    | "Digitale Lösung gesucht"
    | "Sonstiges"
  )[];
  motivation_text: string | null;
  sales_area_available: boolean;
  sales_area_location: string | null;
  project_start: "Sofort" | "Innerhalb von 3 Monaten" | "Später" | "Noch offen";
  partner_talks: "Ja" | "Nein" | "Sonstiges";
  partner_talks_text: string | null;
  concrete_wish: string | null;
};

export const OPTIONAL_TEXT_FIELDS_MUNICIPALITY: ReadonlyArray<keyof MunicipalityUiForm> = [
  "phone",
  "kms_next_supply",
  "motivation_text",
  "sales_area_location",
  "partner_talks_text",
  "concrete_wish",
] as const;

export const POSTAL_CODE_REGEX = /^[0-9]{5}$/;

const baseInputMunicipality = "w-full border px-3 py-3 rounded focus:outline-none focus:ring-2";

export function inputClassMunicipality(
  value: unknown,
  hasError: boolean,
  isTouched: boolean
) {
  const valid =
    isTouched &&
    !hasError &&
    ((typeof value === "string" && value.trim() !== "") ||
      (typeof value === "number" && value > 0) ||
      typeof value === "boolean");
  if (hasError)
    return (
      baseInputMunicipality +
      " !border-red !ring-2 !ring-red focus:!ring-red focus:!border-red !text-red !bg-redLight"
    );
  if (valid) return baseInputMunicipality + " border-lineGreen focus:ring-lineGreen";
  return baseInputMunicipality + " border-gray-300 focus:ring-lineGreen";
}

export function validateMunicipalityUiForm(
  form: MunicipalityUiForm
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.location.trim()) errors.location = "Bitte Ort angeben.";
  if (!POSTAL_CODE_REGEX.test(form.postal_code)) errors.postal_code = "PLZ muss 5 Ziffern haben.";
  if (form.population === "" || (typeof form.population === "number" && form.population <= 0))
    errors.population = "Einwohnerzahl größer 0 erforderlich.";
  if (!form.contact_name.trim()) errors.contact_name = "Kontaktname fehlt.";
  if (!form.contact_role.trim()) errors.contact_role = "Funktion fehlt.";
  if (!form.email.trim()) errors.email = "E-Mail fehlt.";
  if (form.local_supplies.length === 0)
    errors.local_supplies = "Bitte mindestens ein Angebot auswählen.";
  if (form.motivation.length === 0) errors.motivation = "Bitte Motivation auswählen.";
  if (form.motivation.includes("Sonstiges") && !(form.motivation_text ?? "").trim())
    errors.motivation_text = "Bitte Motivation erläutern.";
  if (form.sales_area_available && !(form.sales_area_location ?? "").trim())
    errors.sales_area_location = "Adresse der Verkaufsfläche fehlt.";
  if (form.partner_talks === "Sonstiges" && !(form.partner_talks_text ?? "").trim())
    errors.partner_talks_text = "Bitte Partnergespräche erläutern.";
  return errors;
}

export function mapMunicipalityUiToDto(
  form: MunicipalityUiForm
): MunicipalitiesInterestForm {
  return {
    location: form.location.trim(),
    postal_code: form.postal_code.trim(),
    population: typeof form.population === "number" ? form.population : 0,
    contact_name: form.contact_name.trim(),
    contact_role: form.contact_role.trim(),
    email: form.email.trim().toLowerCase(),
    phone: (form.phone ?? "").trim() || null,
    local_supplies: form.local_supplies,
    kms_next_supply: (form.kms_next_supply ?? "").trim() || null,
    motivation: form.motivation,
    motivation_text: (form.motivation_text ?? "").trim() || null,
    sales_area_available: form.sales_area_available,
    sales_area_location: (form.sales_area_location ?? "").trim() || null,
    project_start: form.project_start,
    partner_talks: form.partner_talks,
    partner_talks_text: (form.partner_talks_text ?? "").trim() || null,
    concrete_wish: (form.concrete_wish ?? "").trim() || null,
  };
}
export function validateForm(form: InterestForm): { [key: string]: string } {
  const errors: { [key: string]: string } = {};

  if (!form.contact_name.trim()) errors.contact_name = "Kontaktname ist erforderlich.";
  if (!form.company_name.trim()) errors.company_name = "Firmenname ist erforderlich.";
  if (!form.email.trim()) errors.email = "E-Mail ist erforderlich.";
  if (form.number_of_products <= 0) errors.number_of_products = "Mindestens ein Produkt angeben.";
  if (!form.product_categories.trim()) errors.product_categories = "Produktkategorie ist erforderlich.";
  if (!form.desired_start_date) errors.desired_start_date = "Startdatum ist erforderlich.";
  if (!form.location.trim()) errors.location = "Standort ist erforderlich.";

  return errors;
}

export function validateMunicipalitiesForm(form: MunicipalitiesInterestForm): { [key: string]: string } {
  const errors: { [key: string]: string } = {};

  if (!form.contact_name.trim()) errors.contact_name = "Kontaktname ist erforderlich.";
  if (!form.email.trim()) errors.email = "E-Mail ist erforderlich.";
  if (!form.location.trim()) errors.location = "Standort ist erforderlich.";

  return errors;
}

export function validateContactForm(form: ContactFormData): { [key: string]: string } {
  const errors: { [key: string]: string } = {};

  if (!form.name.trim()) errors.name = "Kontaktname ist erforderlich.";
  if (!form.email.trim()) errors.email = "E-Mail ist erforderlich.";
  if (!form.concern) errors.concern = "Bitte wählen Sie ein Anliegen.";
  if (!form.subject.trim()) errors.subject = "Betreff ist erforderlich.";
  if (!form.message.trim()) errors.message = "Nachricht ist erforderlich.";

  return errors;
}

export const MUNICIPALITY_DEFAULT_UI_FORM: MunicipalityUiForm = {
  location: "",
  postal_code: "",
  population: "",
  contact_name: "",
  contact_role: "",
  email: "",
  phone: null,
  local_supplies: [],
  kms_next_supply: null,
  motivation: [],
  motivation_text: null,
  sales_area_available: false,
  sales_area_location: null,
  project_start: "Noch offen",
  partner_talks: "Nein",
  partner_talks_text: null,
  concrete_wish: null,
};

export function sanitizePostalCode(input: string): string {
  return input.replace(/\D/g, "").slice(0, 5);
}

export function parsePopulationValue(raw: string): number | "" {
  return raw === "" ? "" : Number(raw);
}

export function normalizeOptional<T extends MunicipalityUiForm, K extends keyof T>(
  form: T,
  name: K,
  raw: string
): T {
  const isOptional = (OPTIONAL_TEXT_FIELDS_MUNICIPALITY as readonly (keyof MunicipalityUiForm)[])
    .includes(name as keyof MunicipalityUiForm);

  const value = (raw === "" && isOptional ? null : raw) as T[K];
  return { ...form, [name]: value };
}

export const POPULATION_STEP = 100;
export const MIN_POPULATION = 1;

export function stepPopulation(current: number | "", direction: "up" | "down"): number {
  const cur = typeof current === "number" ? current : 0;
  return direction === "up" ? cur + POPULATION_STEP : Math.max(0, cur - POPULATION_STEP);
}

export function validateYobstiSuggestionForm(form: SuggestionForm): { [key: string]: string } {
  const errors: { [key: string]: string } = {};

  if (!form.name.trim()) {
    errors.name = 'Bitte gib deinen Namen ein.';
  }

  if (!form.email.trim()) {
    errors.email = 'Bitte gib eine E-Mail-Adresse ein.';
  } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email)) {
    errors.email = 'Bitte gib eine gültige E-Mail-Adresse ein.';
  }

  if (!form.location.trim()) {
    errors.location = 'Bitte gib deinen Ort ein.';
  }

  if (!form.message.trim()) {
    errors.message = 'Bitte gib eine kurze Erklärung ein.';
  }

  return errors;
}
